import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./rendimentiCumulatiPerHoldingPeriod.module.scss";
import RendimentiCumulatiPerHoldingPeriodLegend from "./RendimentiCumulatiPerHoldingPeriodLegend";
const MIN_BAR_WIDTH = 24;

interface RendimentiCumulatiPerHoldingPeriodProps {
  data: any;
  title: string;
  mainColor?: string;
  mainColorFill?: string;
}

// Original data of boxplot graph
type BoxPlot = {
  min: number;
  lowerQuartile: number;
  median: number;
  upperQuartile: number;
  max: number;
  average?: number;
};

type BoxPlotData = {
  min: number;
  bottomWhisker: number;
  bottomBox: number;
  topBox: number;
  topWhisker: number;
  average?: number;
  size: number; // for average dot size
};

const HorizonBar = (props: any) => {
  let { x, y, width = 0, height = 0 } = props;

  if (x == null || y == null) return null;

  // Force minimum width
  const correctedWidth = Math.max(width, MIN_BAR_WIDTH);
  const delta = correctedWidth - width;
  x = x - delta / 2; // center it

  const lineWidth = correctedWidth / 2;
  const xStart = x + (correctedWidth - lineWidth) / 2;
  const yCenter = y + height / 2;

  return (
    <line
      x1={xStart}
      y1={yCenter}
      x2={xStart + lineWidth}
      y2={yCenter}
      stroke={props.mainChartColor} //{colorsConfig.FinecoBluePrimary}
      strokeWidth={1}
    />
  );
};

const DotBar = (props: any) => {
  const { x, y, width, height } = props;

  if (x == null || y == null || width == null || height == null) {
    return null;
  }

  return (
    <line
      x1={x + width / 2}
      y1={y + height}
      x2={x + width / 2}
      y2={y}
      stroke={props.mainChartColor} //{colorsConfig.FinecoBluePrimary}
      strokeWidth={1}
      // strokeDasharray={"5"}
    />
  );
};
const AverageDot = (props: any) => {
  const { cx, cy } = props;

  if (cx == null || cy == null) return null;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={6}
      stroke={props.mainChartColor} //{colorsConfig.FinecoBluePrimary}
      strokeWidth={1}
      fill="white"
    />
  );
};

const useBoxPlot = (boxPlots: BoxPlot[]): BoxPlotData[] => {
  const data = useMemo(
    () =>
      boxPlots.map((v, index) => {
        return {
          min: v.min,
          bottomWhisker: v.lowerQuartile - v.min,
          bottomBox: v.median - v.lowerQuartile,
          topBox: v.upperQuartile - v.median,
          topWhisker: v.max - v.upperQuartile,
          average: v.average,
          size: 250,
          holdingPeriod: index + 1,
        };
      }),
    [boxPlots],
  );

  return data;
};

const RendimentiCumulatiPerHoldingPeriod: React.FC<
  RendimentiCumulatiPerHoldingPeriodProps
> = ({ data, title, mainColor, mainColorFill }) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const mainChartColor = mainColor ? mainColor : colorsConfig.FinecoBluePrimary;
  const rendimentiData: any =
    data.analisi_backtest.rendimenti_cumulati_portafoglio_per_holding_period;
  const boxPlots: BoxPlot[] = Object.values(rendimentiData).map(
    (statArray: any) => {
      const map = Object.fromEntries(
        statArray.map(({ label, weight }) => [label, weight]),
      );

      return {
        min: map["Min"],
        lowerQuartile: map["25 pct"],
        median: map["Median"],
        upperQuartile: map["75 pct"],
        max: map["Max"],
        average: map["Mean"],
      };
    },
  );
  const dataToBePlot = useBoxPlot(boxPlots);

  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Arial",
  };

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.sectionTitle}>{title}</div>

      <div className={styles.sectionWrapper}>
        <div className={styles.chartWrapper}>
          <div className={styles.verticalLabel}>
            {t("portfolio.rendimentoCumulato")}
          </div>
          <div className={styles.chartContainer}>
            <ResponsiveContainer
              width="100%"
              height="100%"
              minHeight={240}
              debounce={500}
            >
              <ComposedChart
                margin={{ top: 20, right: 10, left: -10, bottom: 0 }}
                data={dataToBePlot}
                barCategoryGap={0}
                maxBarSize={24}
              >
                <CartesianGrid
                  strokeWidth={1}
                  stroke={colorsConfig.GrayLight2}
                  vertical={false}
                />

                <ReferenceLine
                  y={0}
                  stroke={colorsConfig.finecoBlack}
                  strokeWidth={1}
                />
                <Bar
                  stackId={"a"}
                  dataKey={"min"}
                  fill={"none"}
                  isAnimationActive={false}
                />
                <Bar
                  stackId={"a"}
                  dataKey={"bar"}
                  shape={<HorizonBar mainChartColor={mainChartColor} />}
                  isAnimationActive={false}
                />
                <Bar
                  stackId={"a"}
                  dataKey={"bottomWhisker"}
                  shape={<DotBar mainChartColor={mainChartColor} />}
                  isAnimationActive={false}
                />
                <Bar
                  stackId={"a"}
                  dataKey={"bottomBox"}
                  fill={mainColorFill}
                  stroke={mainChartColor} //{colorsConfig.BlueDark}
                  strokeWidth={1}
                  isAnimationActive={false}
                />
                <Bar
                  stackId={"a"}
                  dataKey={"bar"}
                  shape={<HorizonBar mainChartColor={mainChartColor} />}
                  isAnimationActive={false}
                />
                <Bar
                  stackId={"a"}
                  dataKey={"topBox"}
                  fill={mainColorFill}
                  stroke={mainChartColor} //{colorsConfig.BlueDark}
                  strokeWidth={1}
                  isAnimationActive={false}
                />
                <Bar
                  stackId={"a"}
                  dataKey={"bar"}
                  shape={<HorizonBar mainChartColor={mainChartColor} />}
                  isAnimationActive={false}
                />
                <Bar
                  stackId={"a"}
                  dataKey={"topWhisker"}
                  shape={<DotBar mainChartColor={mainChartColor} />}
                  isAnimationActive={false}
                />
                <Bar
                  stackId={"a"}
                  dataKey={"bar"}
                  shape={<HorizonBar mainChartColor={mainChartColor} />}
                  isAnimationActive={false}
                />
                <ZAxis type="number" dataKey="size" range={[0, 250]} />

                <Scatter
                  dataKey="average"
                  shape={<AverageDot mainChartColor={mainChartColor} />}
                  isAnimationActive={false}
                />
                <XAxis
                  dataKey="holdingPeriod"
                  tick={chartCopy}
                  tickLine={false}
                  axisLine={{ stroke: colorsConfig.GrayLight2 }}
                />
                <YAxis
                  tick={chartCopy}
                  tickLine={false}
                  axisLine={{ stroke: colorsConfig.GrayLight2 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className={styles.xAxisLabel}>
          {t("portfolio.orizzonteTemporale")}
        </div>
        <RendimentiCumulatiPerHoldingPeriodLegend />
      </div>
    </div>
  );
};

export default RendimentiCumulatiPerHoldingPeriod;
